import { useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Empty, InputNumber, Modal, Space, Tooltip, Typography } from 'antd';
import { nhomNghin } from '../../format';
import { DataTable } from '../../components/DataTable';
import { Calculator, Question, UploadSimple } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { catalogApi, catalogKeys } from '../catalog/api';
import {
  describeVersionState,
  diffRules,
  earningApi,
  earningKeys,
  type EarningRuleVersion,
} from './api';
import { RuleFormPanel, type RuleFormPanelHandle, type SubmitValues } from './RuleFormPanel';
import { tokens } from '../../theme';
import { VND } from '../../format';

function formatDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
}

export function EarningRulesPage() {
  const queryClient = useQueryClient();
  // `modal` từ `App.useApp()` chứ không phải `Modal.confirm` tĩnh: hàm tĩnh không đọc
  // được context nên hộp thoại hiện màu mặc định của AntD, không phải màu Reski.
  const { message, modal } = App.useApp();

  const panelRef = useRef<RuleFormPanelHandle>(null);
  const [editing, setEditing] = useState<EarningRuleVersion | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  // Đổi mỗi lần bấm "Hủy" — ép Form trong RuleFormPanel mount lại (qua `key` bên dưới) để
  // trả về đúng giá trị đã lưu, kể cả khi bản đang hiện không đổi (vẫn là draft đó, chỉ huỷ
  // những gì gõ dở). Không có token này thì `key` không đổi lúc id không đổi, Form không
  // reset, và "Hủy" trông như không làm gì.
  const [formResetToken, setFormResetToken] = useState(0);
  const [comparing, setComparing] = useState<[EarningRuleVersion, EarningRuleVersion] | null>(null);
  const [previewAmount, setPreviewAmount] = useState<number>(500_000);
  // Ô thử tính giờ nằm trong popup, mở từ nút trên PageHeader — trước đó là Card cố định
  // chiếm chỗ phía trên form dù ít khi dùng tới.
  const [previewOpen, setPreviewOpen] = useState(false);

  const versionsQuery = useQuery({
    queryKey: earningKeys.versions(),
    queryFn: () => earningApi.list(),
  });
  const chainsQuery = useQuery({ queryKey: catalogKeys.chains, queryFn: catalogApi.listChains });
  const previewQuery = useQuery({
    queryKey: earningKeys.preview(previewAmount),
    queryFn: () => earningApi.preview(previewAmount),
    enabled: previewAmount > 0,
  });

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['earning-rules'] });
    await queryClient.invalidateQueries({ queryKey: ['earning-preview'] });
  };

  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data]);
  const drafts = versions.filter((v) => !v.isPublished);
  const published = versions.filter((v) => v.isPublished);

  const currentlyEffective = published.find(
    (v) => describeVersionState(v, versions).label === 'Đang áp dụng',
  );

  // Mặc định hiện BẢN NHÁP mới nhất nếu có (giống prototype a-earning: "Phiên bản 4 · đang
  // soạn thảo" là nội dung mặc định), không thì bản đang áp dụng, không thì rỗng.
  const displayedVersion = isCreating ? null : (editing ?? drafts[0] ?? currentlyEffective ?? null);

  const saveMutation = useMutation({
    mutationFn: (values: SubmitValues) =>
      displayedVersion
        ? earningApi.updateDraft(displayedVersion.id, {
            rules: values.rules,
            effectiveFrom: values.effectiveFrom,
            note: values.note,
          })
        : earningApi.createDraft(values),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      setIsCreating(false);
      message.success('Đã lưu bản nháp. Chưa ảnh hưởng giao dịch nào cho tới khi phát hành.');
    },
    onError: (error) => reportError(error, 'Không lưu được bản nháp.'),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => earningApi.publish(id),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      message.success('Đã phát hành. Nội dung bản này khóa lại từ giờ.');
    },
    onError: (error) => reportError(error, 'Không phát hành được.'),
  });

  const retireMutation = useMutation({
    mutationFn: (id: string) => earningApi.retire(id),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      message.success('Đã ngừng. Giao dịch cũ vẫn giải trình theo bản này.');
    },
    onError: (error) => reportError(error, 'Không ngừng được bản này.'),
  });

  const confirmPublish = (version: EarningRuleVersion) => {
    // Bản đang áp dụng hiện tại trong cùng phạm vi, để nói rõ cái gì sẽ bị thay
    const replacing = published
      .filter((v) => v.chainId === version.chainId && v.isCurrentlyEffective)
      .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0];

    modal.confirm({
      title: `Phát hành bản ${version.version}?`,
      width: 560,
      okText: 'Phát hành',
      cancelText: 'Để sau',
      content: (
        <>
          <Typography.Paragraph>
            Từ <strong>{formatDateTime(version.effectiveFrom)}</strong>, giao dịch mới sẽ tính theo
            bản này. Giao dịch cũ giữ nguyên quy tắc đã áp.
          </Typography.Paragraph>
          <Typography.Paragraph type="danger" style={{ marginBottom: 0 }}>
            Sau khi phát hành, <strong>nội dung bản này khóa vĩnh viễn</strong> — mỗi bút toán
            vì những giao dịch đã tính theo nó phải giải trình được về sau. Muốn đổi con số thì tạo bản mới.
          </Typography.Paragraph>
          {replacing && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message={`Thay cho bản ${replacing.version} đang áp dụng`}
              description={
                diffRules(replacing.rules, version.rules).length === 0
                  ? 'Hai bản có nội dung giống nhau — phát hành sẽ không đổi gì.'
                  : `${diffRules(replacing.rules, version.rules).length} tham số thay đổi.`
              }
            />
          )}
        </>
      ),
      onOk: () => publishMutation.mutateAsync(version.id),
    });
  };

  const confirmRetire = (version: EarningRuleVersion) => {
    const fallback = versions.find(
      (v) =>
        !v.chainId &&
        !v.retiredAt &&
        describeVersionState(v, versions).label === 'Đang áp dụng',
    );

    modal.confirm({
      title: `Ngừng bản ${version.version}?`,
      width: 560,
      okText: 'Ngừng áp dụng',
      okButtonProps: { danger: true },
      cancelText: 'Giữ nguyên',
      content: (
        <>
          <Typography.Paragraph>
            Từ lúc này, hóa đơn chuỗi {version.chainName ?? 'này'} tính theo bản mặc định toàn hệ
            thống
            {fallback ? (
              <>
                {' '}
                (<strong>{fallback.version}</strong>
                {fallback.rules.vndPerPoint
                  ? ` · 1 điểm / ${VND.format(fallback.rules.vndPerPoint)}đ`
                  : ''}
                ).
              </>
            ) : (
              '.'
            )}
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Không xóa bản ghi. Giao dịch cũ vẫn tính theo {version.version}. Bản mặc
            định toàn hệ thống không ngừng được — muốn đổi số thì phát hành bản mới.
          </Typography.Paragraph>
        </>
      ),
      onOk: () => retireMutation.mutateAsync(version.id),
    });
  };

  /**
   * Hai bản để so, chọn theo câu hỏi người vận hành thật sự hỏi.
   *
   * Có bản nháp → "phát hành nó thì đổi gì" (nháp so với bản đang chạy).
   * Không có nháp → "lần đổi vừa rồi đổi gì" (bản đang chạy so với bản trước nó).
   *
   * `null` khi không có gì để so — nút tắt hẳn thay vì bấm rồi không thấy gì.
   */
  const comparePair = useMemo<[EarningRuleVersion, EarningRuleVersion] | null>(() => {
    const draft = drafts[0];
    if (draft && currentlyEffective) return [currentlyEffective, draft];

    const byEffective = [...published].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
    return byEffective.length >= 2 ? [byEffective[1], byEffective[0]] : null;
  }, [drafts, published, currentlyEffective]);

  // Đọc lại đúng logic của RuleFormPanel để quyết định 3 nút Hủy/Lưu/Phát hành ở PageHeader —
  // giống prototype đặt chúng trên đầu trang thay vì cuối form (đổi 2026-09-01).
  const readOnly = displayedVersion?.isPublished === true;
  const hasContent = isCreating || displayedVersion !== null;

  return (
    <>
      <PageHeader
        title="Quy tắc tích điểm"
        subtitle="Có phiên bản · đã phát hành thì khóa nội dung · giao dịch cũ không bị tính lại"
        actions={
          <Space>
            <Button icon={<Calculator size={14} aria-hidden />} onClick={() => setPreviewOpen(true)}>
              Thử tính
            </Button>
            {hasContent && !readOnly && (
              <>
                <Button
                  onClick={() => {
                    setEditing(null);
                    setIsCreating(false);
                    setFormResetToken((t) => t + 1);
                  }}
                  disabled={saveMutation.isPending}
                >
                  Hủy
                </Button>
                <Button type="primary" loading={saveMutation.isPending} onClick={() => panelRef.current?.submit()}>
                  {displayedVersion ? 'Lưu bản nháp' : 'Tạo bản nháp'}
                </Button>
                {displayedVersion && (
                  <Button
                    icon={<UploadSimple size={14} aria-hidden />}
                    loading={publishMutation.isPending}
                    onClick={() => confirmPublish(displayedVersion)}
                  >
                    Phát hành
                  </Button>
                )}
              </>
            )}
          </Space>
        }
      />
      <PageBody>
        {versionsQuery.isPending && <LoadingState />}

        {versionsQuery.isError && (
          <ErrorState error={versionsQuery.error} onRetry={() => void versionsQuery.refetch()} />
        )}

        {versionsQuery.isSuccess && (
          <>
            <RuleFormPanel
              ref={panelRef}
              key={`${displayedVersion?.id ?? 'new'}-${formResetToken}`}
              version={displayedVersion}
              isCreating={isCreating}
              chains={chainsQuery.data ?? []}
              submitting={saveMutation.isPending}
              onSubmit={(values) => saveMutation.mutate(values)}
              onRetire={() => {
                if (displayedVersion) confirmRetire(displayedVersion);
              }}
              retiring={retireMutation.isPending}
              versions={versions}
              selectedId={displayedVersion?.id ?? null}
              onSelectVersion={(v) => {
                setEditing(v);
                setIsCreating(false);
              }}
              onCreateNew={() => {
                setEditing(null);
                setIsCreating(true);
              }}
              onCompare={() => setComparing(comparePair)}
              compareDisabled={comparePair === null}
            />
          </>
        )}
      </PageBody>

      <Modal
        open={previewOpen}
        width={560}
        title={
          <Space>
            <Calculator size={16} />
            Thử tính theo bản đang áp dụng
          </Space>
        }
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        destroyOnHidden
      >
        <Space align="start" wrap>
          <div>
            <label
              htmlFor="preview-amount"
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginBottom: 4 }}
            >
              Số tiền hóa đơn (đồng)
              <Tooltip title="Ô thử tính chưa truyền hạng khách nên luôn ra hệ số 1,0. Đường quét hóa đơn thật thì đã đọc hạng và nhân đúng hệ số.">
                <Question size={15} color={tokens.color.ink3} aria-hidden />
              </Tooltip>
            </label>
            <InputNumber
              id="preview-amount"
              value={previewAmount}
              onChange={(value) => setPreviewAmount(value ?? 0)}
              min={0}
              step={100_000}
              style={{ width: 200 }}
              formatter={nhomNghin.formatter}
              parser={nhomNghin.parser}
            />
          </div>
          <div style={{ maxWidth: 640, paddingTop: 22 }}>
            {previewQuery.isPending && <span style={{ color: tokens.color.ink3 }}>Đang tính...</span>}
            {previewQuery.isError && (
              <span style={{ color: tokens.color.danger }}>
                {previewQuery.error instanceof ApiError
                  ? previewQuery.error.message
                  : 'Không tính được.'}
              </span>
            )}
            {previewQuery.isSuccess && (
              <>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {previewQuery.data.spendablePoints} điểm tiêu được ·{' '}
                  {previewQuery.data.tierPoints} điểm xét hạng
                </div>
                {/* Câu giải thích do SERVER sinh — client không tự nhân chia, để
                    không có hai công thức lệch nhau */}
                <div style={{ fontSize: 12.5, color: tokens.color.ink2 }}>
                  {previewQuery.data.explanation}
                </div>
              </>
            )}
          </div>
        </Space>
      </Modal>

      <Modal
        open={comparing !== null}
        width={720}
        title={comparing ? `So sánh bản ${comparing[0].version} với bản ${comparing[1].version}` : ''}
        footer={null}
        onCancel={() => setComparing(null)}
        destroyOnHidden
      >
        {comparing &&
          (() => {
            const rows = diffRules(comparing[0].rules, comparing[1].rules);
            if (rows.length === 0) {
              return <Empty description="Hai bản có nội dung giống nhau." />;
            }
            return (
              <DataTable toolbar={false}
                rowKey="label"
                size="small"
                pagination={false}
                dataSource={rows}
                columns={[
                  { title: 'Tham số', dataIndex: 'label', width: 220 },
                  { title: `Bản ${comparing[0].version}`, dataIndex: 'before' },
                  {
                    title: `Bản ${comparing[1].version}`,
                    dataIndex: 'after',
                    render: (value: string) => <strong>{value}</strong>,
                  },
                ]}
              />
            );
          })()}
      </Modal>
    </>
  );
}
